export const KPI_COLORS = ["#0d6efd", "#fd7e14", "#212529"];
const DASHBOARD_AXIS_TEXT_COLOR = "#000000";
const DASHBOARD_GRID_COLOR = "#cbd5e1";

const TIMELINE_VERTICAL_GUIDE_COLOR = "rgba(148, 163, 184, 0.12)";

// Cấu hình Apex biểu đồ KPI
export function buildKpiChartOptions({ categories, targetPercent }) {
  return {
    chart: {
      type: /** @type {"line"} */ ("line"),
      height: 350,
      toolbar: { show: false },
    },
    stroke: {
      width: [0, 0, 3],
      curve: "smooth",
    },
    colors: KPI_COLORS,
    dataLabels: {
      enabled: true,
      enabledOnSeries: [0, 1, 2],
      formatter: value => Number(value).toFixed(0),
      style: { fontSize: "11px", fontWeight: 600 },
    },
    plotOptions: {
      bar: {
        columnWidth: "42%",
        borderRadius: 4,
      },
    },
    xaxis: {
      categories,
      labels: {
        style: {
          colors: categories.map(() => DASHBOARD_AXIS_TEXT_COLOR),
          fontWeight: 600,
        },
      },
    },
    yaxis: [
      {
        title: { text: "Số đơn" },
        labels: {
          style: {
            colors: [DASHBOARD_AXIS_TEXT_COLOR],
            fontWeight: 600,
          },
        },
      },
      {
        opposite: true,
        min: 0,
        max: 100,
        tickAmount: 5,
        labels: {
          formatter: value => `${Math.round(value)}%`,
          style: {
            colors: [DASHBOARD_AXIS_TEXT_COLOR],
            fontWeight: 600,
          },
        },
        title: { text: "Tỷ lệ đạt KPI" },
      },
    ],
    annotations: {
      yaxis: [
        {
          y: targetPercent,
          yAxisIndex: 1,
          borderColor: "#dc3545",
          strokeDashArray: 6,
          label: {
            borderColor: "#dc3545",
            style: {
              color: "#fff",
              background: "#dc3545",
              fontSize: "11px",
            },
            text: `Target ${targetPercent}%`,
          },
        },
      ],
    },
    legend: { position: "top", horizontalAlign: "left" },
    tooltip: {
      shared: true,
      y: {
        formatter: (value, context) =>
          context.seriesIndex === 2 ? `${Number(value).toFixed(1)}%` : `${Number(value).toFixed(0)} đơn`,
      },
    },
  };
}

const TIMELINE_X_AXIS_STROKE = "#000000";

const TIMELINE_GRID_END_PADDING_PX = 48;

export const TIMELINE_CHART_HEIGHT_PX = 416;

// Bước trục Y (1/2/5×10^n) từ max gần đúng
function computeNiceYAxisStep(maxValue) {
  if (maxValue <= 5) return 1;
  const roughStep = maxValue / 5;
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const normalized = roughStep / magnitude;
  let niceNorm;
  if (normalized <= 1) niceNorm = 1;
  else if (normalized <= 2) niceNorm = 2;
  else if (normalized <= 3) niceNorm = 3;
  else if (normalized <= 4) niceNorm = 4;
  else if (normalized <= 5) niceNorm = 5;
  else if (normalized <= 6) niceNorm = 6;
  else if (normalized <= 8) niceNorm = 8;
  else niceNorm = 10;
  return niceNorm * magnitude;
}

// Max trục Y timeline: thêm headroom (ít thì +1, nhiều thì ~10%)
export function computeNiceYAxisBounds(dataMax) {
  const safeMax = Math.max(0, dataMax);
  const headroom = safeMax <= 20
    ? 1
    : Math.max(1, Math.ceil(safeMax * 0.1));
  const withHeadroom = safeMax + headroom;
  const step = computeNiceYAxisStep(withHeadroom);
  const niceMax = Math.ceil(withHeadroom / step) * step;
  return { max: niceMax, tickAmount: Math.round(niceMax / step) };
}

// Tọa độ X spine trục Y trong không gian SVG
function getYAxisSpineLocalX(root) {
  const svgEl = root.querySelector("svg.apexcharts-svg");
  const graphical = root.querySelector(".apexcharts-graphical, .apexcharts-inner");
  if (!svgEl || !graphical) return null;

  const yaxisEl = root.querySelector(".apexcharts-yaxis:not(.apexcharts-yaxis-inversed)");
  if (!yaxisEl) return null;

  const lines = Array.from(yaxisEl.querySelectorAll("line"));
  const spineEl = lines.reduce(
    /** @param {SVGLineElement | null} best @param {SVGLineElement} l */
    (best, l) => {
      const lx1 = parseFloat(l.getAttribute("x1") || "0");
      const lx2 = parseFloat(l.getAttribute("x2") || "0");
      const ly1 = parseFloat(l.getAttribute("y1") || "0");
      const ly2 = parseFloat(l.getAttribute("y2") || "0");
      const len = Math.abs(ly2 - ly1);
      const bestLen = best ? Math.abs(parseFloat(best.getAttribute("y2") || "0") - parseFloat(best.getAttribute("y1") || "0")) : 0;
      return Math.abs(lx1 - lx2) < 3 && len > bestLen ? l : best;
    },
    /** @type {SVGLineElement | null} */(null)
  );

  if (!spineEl) return null;

  const spineRect = /** @type {SVGLineElement} */ (spineEl).getBoundingClientRect();
  const spineScreenX = spineRect.left + spineRect.width / 2;

  const ctm = graphical.getScreenCTM();
  if (!ctm) return null;

  const pt = svgEl.createSVGPoint();
  pt.x = spineScreenX;
  pt.y = 0;
  return pt.matrixTransform(ctm.inverse()).x;
}

// Kéo đường baseline y=0 sang phải thêm padding lưới
function extendTimelineXBaselineOverPadding(chartContext) {
  const globals = chartContext?.w?.globals;
  const root = chartContext?.el;
  if (!globals || !root) {
    return;
  }

  const line = root.querySelector(".apexcharts-yaxis-annotations line");
  if (!line) {
    return;
  }

  const padHorizontal = Number(globals.padHorizontal) || 0;
  const gridWidth = Number(globals.gridWidth) || 0;
  const inset = TIMELINE_GRID_END_PADDING_PX;

  const spineLocalX = getYAxisSpineLocalX(root);
  const x1 = spineLocalX ?? padHorizontal;
  const x2 = padHorizontal + gridWidth + inset;

  line.setAttribute("x1", String(Math.min(x1, x2)));
  line.setAttribute("x2", String(x2));
  line.removeAttribute("clip-path");
  line.style.clipPath = "none";
}

// Gọi kéo baseline sau 2 frame render
function scheduleExtendTimelineXBaseline(chartContext) {
  if (!chartContext?.el) {
    return;
  }

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      extendTimelineXBaselineOverPadding(chartContext);
    });
  });
}

// Options Apex cho biểu đồ đường timeline dashboard
export function buildTimelineChartOptions({
  categories,
  onPointSelect,
  colorPalette,
  onChartMouseMove,
  dataMax,
}) {
  const { max: yAxisMax, tickAmount: yTickAmount } = computeNiceYAxisBounds(dataMax ?? 0);
  const seriesCount = colorPalette?.length || 6;
  const hollowFill = Array.from({ length: seriesCount }, () => "#ffffff");
  const timelineVerticalGuides = (categories || []).map(category => ({
    x: category,
    borderColor: TIMELINE_VERTICAL_GUIDE_COLOR,
    borderWidth: 1,
    strokeDashArray: 0,
    label: {
      text: "",
      borderWidth: 0,
      style: { fontSize: "0px" },
    },
  }));

  return {
    chart: {
      type: /** @type {"line"} */ ("line"),
      height: TIMELINE_CHART_HEIGHT_PX,
      foreColor: DASHBOARD_AXIS_TEXT_COLOR,
      toolbar: { show: false },
      zoom: { enabled: false },
      events: {
        dataPointSelection: onPointSelect,
        mouseMove: onChartMouseMove,
        // Không xử lý mouseLeave trong line chart
        mounted(chartContext) {
          scheduleExtendTimelineXBaseline(chartContext);
        },
        updated(chartContext) {
          scheduleExtendTimelineXBaseline(chartContext);
        },
      },
    },
    colors: colorPalette,
    stroke: { curve: "straight", width: 2 },
    annotations: {
      position: "back",
      xaxis: timelineVerticalGuides,
      yaxis: [
        {
          y: 0,
          yAxisIndex: 0,
          borderColor: TIMELINE_X_AXIS_STROKE,
          borderWidth: 1,
          strokeDashArray: 0,
          width: "100%",
          offsetX: 0,
          offsetY: 0,
          label: {
            borderWidth: 0,
            text: "",
            style: {
              cssClass: "timeline-x-baseline-label-hidden",
              fontSize: "0px",
              padding: { left: 0, right: 0, top: 0, bottom: 0 },
            },
          },
        },
      ],
    },
    markers: {
      size: 5,
      shape: "circle",
      strokeWidth: 2,
      strokeColors: colorPalette,
      colors: hollowFill,
      fillColors: hollowFill,
      fillOpacity: 1,
      hover: { sizeOffset: 3 },
    },
    dataLabels: { enabled: false },
    legend: { show: false },
    xaxis: {
      categories,
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: true,
        color: "#000000",
      },
      labels: {
        trim: false,
        style: {
          colors: categories.map(() => DASHBOARD_AXIS_TEXT_COLOR),
          fontSize: "12px",
        },
      },
      crosshairs: {
        show: true,
        width: "barWidth",
        position: "back",
        fill: { type: "solid", color: "#e2e8f0" },
        stroke: { width: 0 },
      },
      tooltip: { enabled: false },
    },
    yaxis: {
      min: 0,
      max: yAxisMax,
      tickAmount: yTickAmount,
      decimalsInFloat: 0,
      axisBorder: {
        show: true,
        color: "#000000",
      },
      axisTicks: {
        show: true,
        color: "#000000",
      },
      labels: {
        formatter: value => `${Math.round(Number(value) || 0)}`,
        offsetY: 0.5,
        style: {
          colors: [DASHBOARD_AXIS_TEXT_COLOR],
          fontSize: "12px",
        },
      },
    },
    tooltip: {
      enabled: true,
      shared: true,
      intersect: false,
      followCursor: false,
      custom: () =>
        '<div class="apexcharts-tooltip-hidden" style="opacity:0;width:0;height:0;padding:0;margin:0;border:0;"></div>',
    },
    grid: {
      borderColor: DASHBOARD_GRID_COLOR,
      strokeDashArray: 0,
      xaxis: {
        lines: {
          show: true,
        },
      },
      yaxis: {
        lines: {
          show: false,
        },
      },
      padding: {
        left: TIMELINE_GRID_END_PADDING_PX,
        right: TIMELINE_GRID_END_PADDING_PX,
      },
    },
  };
}
